import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// ===============================================
// 0. HELPER FUNCTIONS
// ===============================================

// Safely destructures data, defaulting to an empty object if null
const safeData = (data) => data || {};

// Extracts the initials from a full name (e.g., "John Smith" -> "JS")
const getInitials = (fullName = 'NA') => {
    const parts = fullName.split(' ').filter(p => p.length > 0);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    return 'NA';
};

// Reusable component to render the profile picture or initials
const ProfileInitials = ({ fullName, styles, profilePicture, borderRadius = 0.01 }) => (
    <View style={{ ...styles.profileImage, overflow: 'hidden' }}>
        {profilePicture ? (
            <Image
                src={profilePicture}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: borderRadius }}
            />
        ) : (
            <Text style={styles.initials}>{getInitials(fullName)}</Text>
        )}
    </View>
);

// ===============================================
// 1. STYLESHEETS FOR ALL 10 TEMPLATES (Modern Focus)
// ===============================================

// --- Template 1: Professional Blue (Original) ---
// --- Template 1: Professional Blue (Dynamic) ---
const getStylesBlue = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { marginBottom: 20, borderBottom: 2, borderBottomColor: theme.headerLineColor, paddingBottom: 10 },
        name: { fontSize: 24, fontWeight: 'bold', color: theme.nameColor, marginBottom: 5 },
        contactInfo: { fontSize: 10, color: theme.secondary, marginTop: 5 },
        section: { marginTop: 15, marginBottom: 10 },
        sectionTitle: { fontSize: 14, fontWeight: 'bold', color: theme.primary, marginBottom: 8, borderBottom: 1, borderBottomColor: '#e5e7eb', paddingBottom: 3 },
        text: { fontSize: 10, color: theme.textColor, lineHeight: 1.5 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.titleColor, marginBottom: 3 },
        itemContainer: { marginBottom: 10 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
        skill: { backgroundColor: '#f3f4f6', color: theme.primary, padding: '4 8', borderRadius: 3, fontSize: 9, marginRight: 5, marginBottom: 5 },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', marginBottom: 10, overflow: 'hidden' },
        initials: { fontSize: 20, color: '#ffffff', fontWeight: 'bold' }
    });
};

// --- Template 2: Minimalist/Clean (Dynamic) ---
const getStylesMinimalist = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { marginBottom: 25, paddingBottom: 5, textAlign: 'center', borderBottom: 1.5, borderBottomColor: theme.headerLineColor },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 10 },
        initials: { color: '#ffffff', fontSize: 20, fontWeight: 'bold' },
        name: { fontSize: 22, fontWeight: 'bold', marginBottom: 3, color: theme.primary },
        contactInfo: { fontSize: 9, marginTop: 2, color: theme.secondary },
        section: { marginBottom: 15 },
        sectionTitle: { fontSize: 13, fontWeight: 'bold', color: theme.primary, marginBottom: 8, borderBottom: 1, borderBottomColor: theme.headerLineColor, paddingBottom: 2, textTransform: 'uppercase' },
        text: { fontSize: 9.5, lineHeight: 1.4 },
        subtitle: { fontSize: 10.5, fontWeight: 'bold', marginBottom: 2, color: theme.primary },
        itemContainer: { marginBottom: 10 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 5 },
        skill: { fontSize: 9, marginRight: 10, marginBottom: 5, padding: '2 0', color: theme.primary }
    });
};

// --- Template 3: Two-Column (Dynamic) ---
const SIDEBAR_WIDTH = '30%';
const MAIN_CONTENT_WIDTH = '70%';
const getStylesTwoColumn = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;

    return StyleSheet.create({
        page: { flexDirection: 'row', fontSize: 10, fontFamily: 'Helvetica', backgroundColor: '#f8fafc' },
        sidebar: {
            width: SIDEBAR_WIDTH,
            backgroundColor: theme.leftColumnBackground,
            padding: 20,
            paddingTop: 40,
            color: theme.sidebarTextColor,
            minHeight: '100%'
        },
        mainContent: {
            width: MAIN_CONTENT_WIDTH,
            padding: 30,
            paddingTop: 40,
            backgroundColor: theme.rightColumnBackground,
            color: theme.mainTextColor
        },
        profileImage: {
            width: customization.imageSize,
            height: customization.imageSize,
            borderRadius: calculatedBorderRadius,
            backgroundColor: theme.primary,
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
            marginBottom: 15
        },
        initials: { color: '#ffffff', fontSize: 24, fontWeight: 'bold' },
        header: { marginBottom: 20 },
        name: { fontSize: 26, fontWeight: 'bold', color: theme.mainSubtitleColor, marginBottom: 5 },
        summary: { fontSize: 10, lineHeight: 1.4, marginBottom: 15, color: theme.mainTextColor },
        sidebarTitle: {
            fontSize: 12,
            fontWeight: 'bold',
            color: theme.leftHeadingColor,
            marginBottom: 5,
            marginTop: 15,
            borderBottom: 1,
            borderBottomColor: theme.leftSeparatorColor,
            paddingBottom: 3,
            textTransform: 'uppercase'
        },
        contactText: { fontSize: 9, marginBottom: 2, color: theme.sidebarTextColor },
        sectionTitle: {
            fontSize: 14,
            fontWeight: 'extrabold',
            color: theme.rightHeadingColor,
            marginBottom: 8,
            textTransform: 'uppercase'
        },
        text: { fontSize: 9.5, color: theme.mainTextColor, lineHeight: 1.5 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.mainSubtitleColor, marginBottom: 3 },
        itemContainer: { marginBottom: 12 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
        skillsList: { marginTop: 5 },
        skillItem: { fontSize: 9, marginBottom: 3, color: theme.sidebarTextColor }
    });
};

// --- Template 4: Geometric (Dynamic) ---
const getStylesGeometric = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { flexDirection: 'row', alignItems: 'center', marginBottom: 20, borderBottom: 3, borderBottomColor: theme.primary, paddingBottom: 10 },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', marginRight: 20 },
        initials: { color: '#ffffff', fontSize: 24, fontWeight: 'bold' },
        nameContainer: { flexGrow: 1 },
        name: { fontSize: 24, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        contactInfo: { fontSize: 9, color: theme.secondary, marginTop: 3 },
        section: { marginTop: 15, marginBottom: 10 },
        sectionTitle: { fontSize: 14, fontWeight: 'bold', color: theme.primary, marginBottom: 8, textTransform: 'uppercase' },
        text: { fontSize: 10, color: theme.textColor, lineHeight: 1.6 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        itemContainer: { marginBottom: 12 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
        skill: { fontSize: 10, backgroundColor: '#f3f4f6', color: theme.primary, padding: '3 6', borderRadius: 4 }
    });
};

// --- Template 5: Tech Stack (Dynamic) ---
const getStylesTechStack = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 30, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { flexDirection: 'row', marginBottom: 20, justifyContent: 'space-between', alignItems: 'flex-start' },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center' },
        initials: { color: '#ffffff', fontSize: 20, fontWeight: 'bold' },
        nameContainer: { flexGrow: 1, marginLeft: 10 },
        name: { fontSize: 22, fontWeight: 'extrabold', color: theme.primary, marginBottom: 3 },
        title: { fontSize: 12, color: theme.primary, marginBottom: 8 },
        contactInfo: { fontSize: 9, color: theme.secondary, lineHeight: 1.5 },
        section: { marginTop: 15, marginBottom: 10, borderTop: 1, borderTopColor: theme.primary, paddingTop: 8 },
        sectionTitle: { fontSize: 13, fontWeight: 'bold', color: theme.primary, marginBottom: 8, textTransform: 'uppercase' },
        text: { fontSize: 9.5, color: theme.secondary, lineHeight: 1.4 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        itemContainer: { marginBottom: 10 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
        skill: { fontSize: 10, color: theme.primary, marginRight: 5, marginBottom: 5 }
    });
};

// --- Template 6: Freelancer (Dynamic) ---
const getStylesFreelancer = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 35 : 0.01;
    return StyleSheet.create({
        page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { marginBottom: 20, textAlign: 'center' },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.secondary, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 10 },
        initials: { color: '#ffffff', fontSize: 30, fontWeight: 'normal' },
        name: { fontSize: 28, fontWeight: 'light', color: theme.primary, marginBottom: 5 },
        contactInfo: { fontSize: 9, color: theme.secondary, marginTop: 5 },
        section: { marginTop: 15, marginBottom: 10 },
        sectionTitle: { fontSize: 15, fontWeight: 'bold', color: theme.primary, marginBottom: 8, borderBottom: 1, borderBottomColor: theme.primary, paddingBottom: 5, textAlign: 'center' },
        text: { fontSize: 9.5, color: theme.textColor, lineHeight: 1.5 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        itemContainer: { marginBottom: 12 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center' },
        skill: { fontSize: 10 }
    });
};

// --- Template 7: Architect (Dynamic) ---
const getStylesArchitect = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 5 : 0.01;
    return StyleSheet.create({
        page: { padding: 30, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { flexDirection: 'row', alignItems: 'center', marginBottom: 20, paddingBottom: 10, borderBottom: 1, borderBottomColor: theme.headerLineColor },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', marginRight: 15 },
        initials: { color: '#ffffff', fontSize: 18, fontWeight: 'bold' },
        name: { fontSize: 28, fontWeight: 'extrabold', color: theme.primary, marginBottom: 2 },
        title: { fontSize: 12, color: theme.primary, marginBottom: 5 },
        contactInfo: { fontSize: 9, color: theme.secondary, lineHeight: 1.5 },
        section: { marginTop: 15, marginBottom: 10, paddingLeft: 10, borderLeft: 3, borderLeftColor: theme.headerLineColor },
        sectionTitle: { fontSize: 14, fontWeight: 'extrabold', color: theme.primary, marginBottom: 8, textTransform: 'uppercase' },
        text: { fontSize: 9.5, color: theme.textColor, lineHeight: 1.5 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        itemContainer: { marginBottom: 12 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 5 },
        skill: { fontSize: 9, marginRight: 15, color: theme.primary }
    });
};

// --- Template 8: Simple Professional (New Modern) ---
const getStylesSimpleProfessional = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { marginBottom: 20, paddingBottom: 5, textAlign: 'center' },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 10 },
        initials: { color: '#ffffff', fontSize: 20, fontWeight: 'bold' },
        name: { fontSize: 26, fontWeight: 'bold', marginBottom: 5, color: theme.primary },
        contactInfo: { fontSize: 9, marginTop: 3, color: theme.secondary },
        section: { marginTop: 15, marginBottom: 10 },
        sectionTitle: { fontSize: 14, fontWeight: 'bold', marginBottom: 6, textTransform: 'uppercase', borderBottom: 1, borderBottomColor: theme.primary, paddingBottom: 2, color: theme.primary },
        text: { fontSize: 9.5, lineHeight: 1.4, color: theme.textColor },
        subtitle: { fontSize: 11, fontWeight: 'bold', marginBottom: 3, color: theme.primary },
        itemContainer: { marginBottom: 10 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 5 },
        skill: { fontSize: 10, color: theme.primary }
    });
};

// --- Template 9: Asymmetrical (New Modern Two-Column) ---
const ASYM_SIDEBAR_WIDTH = '30%';
const ASYM_MAIN_CONTENT_WIDTH = '70%';
const getStylesAsymmetrical = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { flexDirection: 'row', fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground },
        sidebar: { width: ASYM_SIDEBAR_WIDTH, padding: 25, paddingTop: 40, backgroundColor: theme.leftColumnBackground, color: theme.sidebarTextColor, minHeight: '100%' },
        mainContent: { width: ASYM_MAIN_CONTENT_WIDTH, padding: 30, paddingTop: 40, backgroundColor: theme.rightColumnBackground, color: theme.mainTextColor },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 15 },
        initials: { color: '#ffffff', fontSize: 32, fontWeight: 'bold' },
        name: { fontSize: 20, fontWeight: 'bold', color: theme.leftHeadingColor, marginBottom: 5, textAlign: 'center' },
        sidebarTitle: { fontSize: 12, fontWeight: 'bold', color: theme.leftHeadingColor, marginBottom: 5, marginTop: 15, textTransform: 'uppercase', borderBottom: 1, borderBottomColor: theme.leftSeparatorColor, paddingBottom: 3 },
        contactText: { fontSize: 9, marginBottom: 2, color: theme.sidebarTextColor },
        sectionTitle: { fontSize: 14, fontWeight: 'extrabold', color: theme.rightHeadingColor, marginBottom: 8, textTransform: 'uppercase' },
        text: { fontSize: 9.5, color: theme.mainTextColor, lineHeight: 1.5 },
        subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.mainSubtitleColor, marginBottom: 3 },
        itemContainer: { marginBottom: 12 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
        skillsList: { marginTop: 5 },
        skillItem: { fontSize: 9, marginBottom: 3, paddingLeft: 5, color: theme.sidebarTextColor }
    });
};

// --- Template 10: Vibrant Compact (New Modern) ---
const getStylesVibrantCompact = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { padding: 30, fontSize: 9, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
        header: { flexDirection: 'row', alignItems: 'center', marginBottom: 15, paddingBottom: 5, borderBottom: 5, borderBottomColor: theme.primary },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: theme.primary, justifyContent: 'center', alignItems: 'center', marginRight: 15 },
        initials: { color: '#ffffff', fontSize: 16, fontWeight: 'bold' },
        name: { fontSize: 20, fontWeight: 'bold', color: theme.primary, marginBottom: 3 },
        contactInfo: { fontSize: 8.5, color: theme.secondary, marginTop: 3, lineHeight: 1.3 },
        section: { marginTop: 12, marginBottom: 8 },
        sectionTitle: { fontSize: 12, fontWeight: 'bold', color: theme.primary, marginBottom: 6, textTransform: 'uppercase' },
        text: { fontSize: 8.5, color: theme.textColor, lineHeight: 1.3 },
        subtitle: { fontSize: 10, fontWeight: 'bold', color: theme.primary, marginBottom: 2 },
        itemContainer: { marginBottom: 8 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
        skillsContainer: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 3 },
        skill: { fontSize: 8.5, marginRight: 5, padding: '2 5', backgroundColor: theme.secondary + '20', borderRadius: 2, color: theme.primary }
    });
};


// ===============================================
// 2. TEMPLATE RENDER FUNCTIONS (ALL 10)
// ===============================================

const Watermark = ({ isPaid }) => (
    <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        opacity: isPaid ? 0 : 0.3,
        transform: 'rotate(-45deg)'
    }} fixed>
        {Array.from({ length: 6 }).map((_, i) => (
            <Text key={i} style={{ fontSize: 60, color: '#6b7280', fontWeight: 'bold', margin: 20 }}>
                quick-hire.org
            </Text>
        ))}
    </View>
);

// --- Template 1: Professional Blue (Function) ---
const RenderBlue = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#1e40af',
        secondary: colors.textColor || colors.secondary || '#4b5563',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#333333',
        nameColor: colors.titleColor || colors.nameColor || (colors.primary || '#1e40af'),
        titleColor: colors.titleColor || '#1f2937',
        headerLineColor: colors.titleColor || colors.headerLineColor || (colors.primary || '#1e40af'),
    };
    const customConfig = {
        imageSize: customization.imageSize || 50,
        imageShape: customization.imageShape || 'square',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesBlue(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View>
                        <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                        <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone}</Text>
                    </View>
                    <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                </View>
                {(personal_info.address || personal_info.linkedin || personal_info.website) && (
                    <Text style={styles.contactInfo}>
                        {personal_info.address && `${personal_info.address} `}
                        {personal_info.linkedin && `| LinkedIn: ${personal_info.linkedin} `}
                        {personal_info.website && `| Website: ${personal_info.website}`}
                    </Text>
                )}
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>PROFESSIONAL SUMMARY</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>WORK EXPERIENCE</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{exp.position} at {exp.company}</Text>
                        <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>EDUCATION</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                        <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{edu.institution}</Text>
                    {edu.description && (<Text style={styles.text}>{edu.description}</Text>)}
                </View>))}</View>
            )}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>SKILLS</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}>{skill}</Text>))}
                </View></View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};



// --- Template 2: Minimalist/Clean (Function) ---
const RenderMinimalist = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#3b82f6',
        secondary: colors.textColor || colors.secondary || '#6b7280',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#333333',
        headerLineColor: colors.titleColor || colors.headerLineColor || '#222222',
    };
    const customConfig = {
        imageSize: customization.imageSize || 50,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesMinimalist(theme, customConfig);

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone} | {personal_info.address}</Text>
                <Text style={styles.contactInfo}>
                    {personal_info.linkedin && `LinkedIn: ${personal_info.linkedin}`}
                    {personal_info.website && ` | Website: ${personal_info.website}`}
                </Text>
            </View>

            {personal_info.summary && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Summary</Text>
                    <Text style={styles.text}>{personal_info.summary}</Text>
                </View>
            )}

            {work_experience.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Experience</Text>
                    {work_experience.map((exp, index) => (
                        <View key={index} style={styles.itemContainer}>
                            <View style={styles.flexRow}>
                                <Text style={styles.subtitle}>{exp.position}</Text>
                                <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                            </View>
                            <Text style={styles.subtitle}>{exp.company}</Text>
                            <Text style={styles.text}>{exp.description}</Text>
                        </View>
                    ))}
                </View>
            )}

            {education.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Education</Text>
                    {education.map((edu, index) => (
                        <View key={index} style={styles.itemContainer}>
                            <View style={styles.flexRow}>
                                <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                                <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                            </View>
                            <Text style={styles.text}>{edu.institution}</Text>
                        </View>
                    ))}
                </View>
            )}

            {skills.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Skills</Text>
                    <View style={styles.skillsContainer}>
                        {skills.map((skill, index) => (
                            <Text key={index} style={styles.skill}>{skill}</Text>
                        ))}
                    </View>
                </View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 3: Two-Column (Function) ---
const RenderTwoColumn = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);

    // Build comprehensive theme with all customization options
    const baseHeadingColor = colors.titleColor || colors.headingColor || colors.primary || '#0f172a';
    const baseSeparatorColor = colors.titleColor || colors.separatorColor || '#94a3b8';
    const baseTextColor = colors.textColor || '#333333';
    const baseSubtitleColor = colors.textColor || colors.secondary || '#1f2937';

    const theme = {
        primary: colors.titleColor || colors.primary || '#0f172a',
        secondary: colors.textColor || colors.secondary || '#64748b',

        // Sidebar (Left - 30%)
        leftColumnBackground: colors.sidebarBackgroundColor || colors.leftColumnBackground || '#e2e8f0',
        leftHeadingColor: colors.sidebarTitleColor || colors.leftHeadingColor || baseHeadingColor,
        leftSeparatorColor: colors.sidebarTitleColor || colors.leftSeparatorColor || baseSeparatorColor,
        sidebarTextColor: colors.sidebarTextColor || '#1f2937',

        // Main Content (Right - 70%)
        rightColumnBackground: colors.backgroundColor || colors.rightColumnBackground || '#ffffff',
        rightHeadingColor: colors.titleColor || colors.rightHeadingColor || baseHeadingColor,
        mainTextColor: colors.textColor || colors.mainTextColor || baseTextColor,
        mainSubtitleColor: colors.titleColor || colors.mainSubtitleColor || baseSubtitleColor,
    };

    const customConfig = {
        imageSize: customization.imageSize || 60,
        imageShape: customization.imageShape || 'circle',
    };

    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;

    const styles = getStylesTwoColumn(theme, customConfig);

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.sidebar}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.sidebarTitle}>Contact</Text>
                <Text style={styles.contactText}>{personal_info.email}</Text>
                <Text style={styles.contactText}>{personal_info.phone}</Text>
                <Text style={styles.contactText}>{personal_info.address}</Text>
                {personal_info.linkedin && <Text style={styles.contactText}>LinkedIn: {personal_info.linkedin}</Text>}

                {skills.length > 0 && (
                    <View style={{ marginTop: 20 }}>
                        <Text style={styles.sidebarTitle}>Skills</Text>
                        <View style={styles.skillsList}>
                            {skills.map((skill, index) => (
                                <Text key={index} style={styles.skillItem}>• {skill}</Text>
                            ))}
                        </View>
                    </View>
                )}

                {education.length > 0 && (
                    <View style={{ marginTop: 20 }}>
                        <Text style={styles.sidebarTitle}>Education</Text>
                        {education.map((edu, index) => (
                            <View key={index} style={{ marginBottom: 10 }}>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{edu.degree}</Text>
                                <Text style={{ fontSize: 9 }}>{edu.institution}</Text>
                                <Text style={{ fontSize: 9 }}>{edu.start_date} - {edu.end_date}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>

            <View style={styles.mainContent}>
                <View style={styles.header}>
                    <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                    <Text style={styles.summary}>{personal_info.summary}</Text>
                </View>

                {work_experience.length > 0 && (
                    <View>
                        <Text style={styles.sectionTitle}>Experience</Text>
                        {work_experience.map((exp, index) => (
                            <View key={index} style={styles.itemContainer}>
                                <View style={styles.flexRow}>
                                    <Text style={styles.subtitle}>{exp.position}</Text>
                                    <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                                </View>
                                <Text style={{ fontSize: 10, fontWeight: 'bold', color: theme.primary, marginBottom: 2 }}>{exp.company}</Text>
                                <Text style={styles.text}>{exp.description}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 4: Geometric (Function) ---
const RenderGeometric = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#3f51b5',
        secondary: colors.textColor || colors.secondary || '#555555',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#1a1a1a',
    };
    const customConfig = {
        imageSize: customization.imageSize || 60,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesGeometric(theme, customConfig);

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <View style={styles.nameContainer}>
                    <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                    <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone}</Text>
                    <Text style={styles.contactInfo}>{personal_info.address}</Text>
                </View>
            </View>

            {personal_info.summary && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Summary</Text>
                    <Text style={styles.text}>{personal_info.summary}</Text>
                </View>
            )}

            {work_experience.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Experience</Text>
                    {work_experience.map((exp, index) => (
                        <View key={index} style={styles.itemContainer}>
                            <View style={styles.flexRow}>
                                <Text style={styles.subtitle}>{exp.position}</Text>
                                <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                            </View>
                            <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 2 }}>{exp.company}</Text>
                            <Text style={styles.text}>{exp.description}</Text>
                        </View>
                    ))}
                </View>
            )}

            {education.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Education</Text>
                    {education.map((edu, index) => (
                        <View key={index} style={styles.itemContainer}>
                            <View style={styles.flexRow}>
                                <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                                <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                            </View>
                            <Text style={styles.text}>{edu.institution}</Text>
                        </View>
                    ))}
                </View>
            )}

            {skills.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Skills</Text>
                    <View style={styles.skillsContainer}>
                        {skills.map((skill, index) => (
                            <Text key={index} style={styles.skill}>{skill}</Text>
                        ))}
                    </View>
                </View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};


// --- Template 5: Tech Stack (Function) ---
const RenderTechStack = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#10b981',
        secondary: colors.textColor || colors.secondary || '#4b5563',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#111827',
    };
    const customConfig = {
        imageSize: customization.imageSize || 50,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesTechStack(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <View>
                    <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                    <Text style={styles.title}>{personal_info.resume_title || ''}</Text>
                </View>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
            </View>
            <View style={{ marginBottom: 20, flexDirection: 'row', justifyContent: 'space-around', borderBottom: 1, borderBottomColor: '#f3f4f6', paddingBottom: 10 }}>
                <Text style={styles.contactInfo}>{personal_info.email}</Text>
                <Text style={styles.contactInfo}>{personal_info.phone}</Text>
                <Text style={styles.contactInfo}>{personal_info.linkedin}</Text>
                <Text style={styles.contactInfo}>{personal_info.address}</Text>
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>Profile</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Core Competencies</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}># {skill}</Text>))}
                </View></View>
            )}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Experience</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position}</Text>
                        <Text style={styles.subtitle}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.company}</Text>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Education</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{edu.degree} - {edu.field_of_study}</Text>
                        <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{edu.institution}</Text>
                </View>))}</View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 6: Freelancer (Function) ---
const RenderFreelancer = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#2c3e50',
        secondary: colors.textColor || colors.secondary || '#7f8c8d',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#333333',
    };
    const customConfig = {
        imageSize: customization.imageSize || 70,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 35 : 0.01;
    const styles = getStylesFreelancer(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                <Text style={styles.contactInfo}>{personal_info.phone} | {personal_info.email} | {personal_info.address}</Text>
                {(personal_info.linkedin || personal_info.website) && (<Text style={styles.contactInfo}>
                    {personal_info.linkedin && `LinkedIn: ${personal_info.linkedin}`}
                    {personal_info.linkedin && personal_info.website && ' | '}
                    {personal_info.website && `Website: ${personal_info.website}`}
                </Text>
                )}
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>Professional Summary</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Key Skills</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}>{skill}</Text>))}
                </View></View>
            )}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Experience</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position}</Text>
                        <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.company}</Text>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Education</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                        <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{edu.institution}</Text>
                </View>))}</View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};



// --- Template 7: Architect (Function) ---
const RenderArchitect = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#34495e',
        secondary: colors.textColor || colors.secondary || '#7f8c8d',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#333333',
        headerLineColor: colors.titleColor || colors.headerLineColor || '#f0f0f0',
    };
    const customConfig = {
        imageSize: customization.imageSize || 50,
        imageShape: customization.imageShape || 'rounded',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 5 : 0.01;
    const styles = getStylesArchitect(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <View>
                    <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                    <Text style={styles.title}>{personal_info.resume_title || ''}</Text>
                </View>
            </View>
            <View style={{ marginBottom: 20 }}>
                <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone} | {personal_info.address}</Text>
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>Summary</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Experience</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position}</Text>
                        <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.company}</Text>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Education</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <Text style={styles.subtitle}>{edu.degree} - {edu.field_of_study}</Text>
                    <Text style={styles.text}>{edu.institution}</Text>
                    <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                </View>))}</View>
            )}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Skills</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}>• {skill}</Text>))}
                </View></View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 8: Simple Professional (Function) ---
const RenderSimpleProfessional = ({ data, isPaid }) => {

    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#000000',
        secondary: colors.textColor || colors.secondary || '#555555',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#000000',
        headerLineColor: colors.titleColor || colors.headerLineColor || '#000000',
    };
    const customConfig = {
        imageSize: customization.imageSize || 50,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesSimpleProfessional(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone} | {personal_info.address}</Text>
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>Profile</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Work Experience</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position}, {exp.company}</Text>
                        <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Education</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                        <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{edu.institution}</Text>
                </View>))}</View>
            )}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Skills</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}>{skill}</Text>))}
                </View></View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 9: Asymmetrical (Function) ---
const RenderAsymmetrical = ({ data, isPaid }) => {

    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const baseHeadingColor = colors.titleColor || colors.headingColor || colors.primary || '#000000';
    const baseSeparatorColor = colors.titleColor || colors.separatorColor || '#cccccc';
    const baseTextColor = colors.textColor || '#333333';
    const baseSubtitleColor = colors.textColor || colors.secondary || '#1f2937';
    const theme = {
        primary: colors.titleColor || colors.primary || '#000000',
        secondary: colors.textColor || colors.secondary || '#555555',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        leftColumnBackground: colors.sidebarBackgroundColor || colors.leftColumnBackground || '#fafafa',
        leftHeadingColor: colors.sidebarTitleColor || colors.leftHeadingColor || baseHeadingColor,
        leftSeparatorColor: colors.sidebarTitleColor || colors.leftSeparatorColor || baseSeparatorColor,
        sidebarTextColor: colors.sidebarTextColor || '#333',
        rightColumnBackground: colors.backgroundColor || colors.rightColumnBackground || '#ffffff',
        rightHeadingColor: colors.titleColor || colors.rightHeadingColor || baseHeadingColor,
        mainTextColor: colors.textColor || colors.mainTextColor || baseTextColor,
        mainSubtitleColor: colors.titleColor || colors.mainSubtitleColor || baseSubtitleColor,
    };
    const customConfig = {
        imageSize: customization.imageSize || 80,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesAsymmetrical(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.sidebar}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                <Text style={styles.sidebarTitle}>Contact</Text>
                {personal_info.phone && <Text style={styles.contactText}>Phone: {personal_info.phone}</Text>}
                {personal_info.email && <Text style={styles.contactText}>Email: {personal_info.email}</Text>}
                {personal_info.address && <Text style={styles.contactText}>Location: {personal_info.address}</Text>}
                {skills.length > 0 && (<View><Text style={styles.sidebarTitle}>Skills</Text>
                    <View style={styles.skillsList}>
                        {skills.map((skill, index) => (<Text key={index} style={styles.skillItem}>• {skill}</Text>))}
                    </View></View>
                )}
                {education.length > 0 && (<View><Text style={styles.sidebarTitle}>Education</Text>
                    {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                        <Text style={styles.contactText}>{edu.institution}</Text>
                        <Text style={styles.contactText}>{edu.degree} - {edu.field_of_study}</Text>
                        <Text style={styles.contactText}>{edu.start_date} - {edu.end_date}</Text>
                    </View>))}</View>
                )}
            </View>
            <View style={styles.mainContent}>
                <View style={styles.header}>
                    <Text style={styles.text}>{personal_info.summary}</Text>
                </View>
                {work_experience.length > 0 && (<View><Text style={styles.sectionTitle}>Work Experience</Text>
                    {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                        <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position} | {exp.company}</Text>
                            <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                        </View>
                        <Text style={styles.text}>{exp.description}</Text>
                    </View>))}</View>
                )}
            </View>
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 10: Vibrant Compact (Function) ---
const RenderVibrantCompact = ({ data, isPaid }) => {

    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const theme = {
        primary: colors.titleColor || colors.primary || '#1f2937',
        secondary: colors.textColor || colors.secondary || '#ef4444',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#1f2937',
    };
    const customConfig = {
        imageSize: customization.imageSize || 40,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesVibrantCompact(theme, customConfig);
    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <View>
                    <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>
                    <Text style={styles.contactInfo}>{personal_info.email} | {personal_info.phone} | {personal_info.address}</Text>
                </View>
            </View>
            {personal_info.summary && (<View style={styles.section}><Text style={styles.sectionTitle}>Summary</Text><Text style={styles.text}>{personal_info.summary}</Text></View>)}
            {skills.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Skills</Text>
                <View style={styles.skillsContainer}>
                    {skills.map((skill, index) => (<Text key={index} style={styles.skill}>{skill}</Text>))}
                </View></View>
            )}
            {work_experience.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Experience</Text>
                {work_experience.map((exp, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}><Text style={styles.subtitle}>{exp.position} at {exp.company}</Text>
                        <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{exp.description}</Text>
                </View>))}</View>
            )}
            {education.length > 0 && (<View style={styles.section}><Text style={styles.sectionTitle}>Education</Text>
                {education.map((edu, index) => (<View key={index} style={styles.itemContainer}>
                    <View style={styles.flexRow}>
                        <Text style={styles.subtitle}>{edu.degree} in {edu.field_of_study}</Text>
                        <Text style={styles.text}>{edu.start_date} - {edu.end_date}</Text>
                    </View>
                    <Text style={styles.text}>{edu.institution}</Text>
                </View>))}</View>
            )}
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// ===============================================
// 3. MAIN SELECTOR COMPONENT
// ===============================================



// --- Template 11: Split Modern (New) ---
const getStylesSplitModern = (theme, customization) => {
    const calculatedBorderRadius = customization.imageShape === 'circle' ? customization.imageSize / 2 :
        customization.imageShape === 'rounded' ? 10 : 0.01;
    return StyleSheet.create({
        page: { flexDirection: 'row', fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground },
        sidebar: { width: '35%', backgroundColor: theme.leftColumnBackground, padding: 25, paddingTop: 40, color: theme.sidebarTextColor, minHeight: '100%' },
        mainContent: { width: '65%', padding: 30, paddingTop: 40, backgroundColor: theme.rightColumnBackground, color: theme.mainTextColor },
        profileImage: { width: customization.imageSize, height: customization.imageSize, borderRadius: calculatedBorderRadius, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 20 },
        initials: { color: '#ffffff', fontSize: 28, fontWeight: 'bold' },
        name: { fontSize: 22, fontWeight: 'bold', color: theme.leftHeadingColor, marginBottom: 5, textAlign: 'center' },
        sidebarTitle: { fontSize: 13, fontWeight: 'bold', color: theme.leftHeadingColor, marginBottom: 10, marginTop: 20, borderBottom: 1, borderBottomColor: theme.leftSeparatorColor, paddingBottom: 5, textTransform: 'uppercase' },
        contactText: { fontSize: 9, marginBottom: 4, color: theme.sidebarTextColor, opacity: 0.9 },
        sectionTitle: { fontSize: 16, fontWeight: 'bold', color: theme.rightHeadingColor, marginBottom: 12, textTransform: 'uppercase', borderBottom: 2, borderBottomColor: theme.rightSeparatorColor, paddingBottom: 5 },
        text: { fontSize: 10, color: theme.mainTextColor, lineHeight: 1.6 },
        subtitle: { fontSize: 12, fontWeight: 'bold', color: theme.mainSubtitleColor, marginBottom: 2 },
        itemContainer: { marginBottom: 15 },
        flexRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 },
        skillItem: { fontSize: 9.5, marginBottom: 4, color: theme.sidebarTextColor, opacity: 0.9 }
    });
};

const RenderSplitModern = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);
    const baseHeadingColor = colors.titleColor || colors.headingColor || colors.primary || '#1e40af';
    const baseSeparatorColor = colors.titleColor || colors.separatorColor || '#f3f4f6';
    const baseTextColor = colors.textColor || '#374151';
    const baseSubtitleColor = colors.textColor || colors.secondary || '#111827';
    const theme = {
        primary: colors.titleColor || colors.primary || '#1e40af',
        secondary: colors.textColor || colors.secondary || '#4b5563',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        leftColumnBackground: colors.sidebarBackgroundColor || colors.leftColumnBackground || (colors.primary || '#1e40af'),
        leftHeadingColor: colors.sidebarTitleColor || colors.leftHeadingColor || '#ffffff',
        leftSeparatorColor: colors.sidebarTitleColor || colors.leftSeparatorColor || 'rgba(255,255,255,0.3)',
        sidebarTextColor: colors.sidebarTextColor || '#ffffff',
        rightColumnBackground: colors.backgroundColor || colors.rightColumnBackground || '#ffffff',
        rightHeadingColor: colors.titleColor || colors.rightHeadingColor || baseHeadingColor,
        rightSeparatorColor: colors.titleColor || colors.rightSeparatorColor || baseSeparatorColor,
        mainTextColor: colors.textColor || colors.mainTextColor || baseTextColor,
        mainSubtitleColor: colors.titleColor || colors.mainSubtitleColor || baseSubtitleColor,
    };
    const customConfig = {
        imageSize: customization.imageSize || 80,
        imageShape: customization.imageShape || 'circle',
    };
    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;
    const styles = getStylesSplitModern(theme, customConfig);

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.sidebar}>
                <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                <Text style={styles.name}>{personal_info.full_name || 'Your Name'}</Text>

                <Text style={styles.sidebarTitle}>Contact</Text>
                {personal_info.phone && <Text style={styles.contactText}>{personal_info.phone}</Text>}
                {personal_info.email && <Text style={styles.contactText}>{personal_info.email}</Text>}
                {personal_info.address && <Text style={styles.contactText}>{personal_info.address}</Text>}
                {personal_info.linkedin && <Text style={styles.contactText}>LinkedIn: {personal_info.linkedin}</Text>}
                {personal_info.website && <Text style={styles.contactText}>Website: {personal_info.website}</Text>}

                {skills.length > 0 && (
                    <View>
                        <Text style={styles.sidebarTitle}>Skills</Text>
                        {skills.map((skill, index) => (
                            <Text key={index} style={styles.skillItem}>• {skill}</Text>
                        ))}
                    </View>
                )}
                {education.length > 0 && (
                    <View>
                        <Text style={styles.sidebarTitle}>Education</Text>
                        {education.map((edu, index) => (
                            <View key={index} style={{ marginBottom: 10 }}>
                                <Text style={styles.contactText}>{edu.degree}</Text>
                                <Text style={styles.contactText}>{edu.institution}</Text>
                                <Text style={styles.contactText}>{edu.start_date} - {edu.end_date}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>
            <View style={styles.mainContent}>
                {personal_info.summary && (
                    <View style={{ marginBottom: 20 }}>
                        <Text style={styles.sectionTitle}>Profile</Text>
                        <Text style={styles.text}>{personal_info.summary}</Text>
                    </View>
                )}
                {work_experience.length > 0 && (
                    <View>
                        <Text style={styles.sectionTitle}>Experience</Text>
                        {work_experience.map((exp, index) => (
                            <View key={index} style={styles.itemContainer}>
                                <View style={styles.flexRow}>
                                    <View>
                                        <Text style={styles.subtitle}>{exp.position}</Text>
                                        <Text style={{ fontSize: 10, color: theme.primary, fontWeight: 'medium' }}>{exp.company}</Text>
                                    </View>
                                    <Text style={styles.text}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                                </View>
                                <Text style={styles.text}>{exp.description}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>
            <Watermark isPaid={isPaid} />
        </Page>
    );
};

// --- Template 12: Sharp Two-Column (Customizable) ---
const getStylesSharpTwoColumn = (theme, customization) => ({
    page: { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: theme.pageBackground, color: theme.textColor },
    header: { marginBottom: 30 },
    headerLine: { height: 4, backgroundColor: theme.headerLineColor, width: '100%', marginBottom: 15 },
    name: { fontSize: 30, fontWeight: 'extraBold', color: theme.nameColor, textTransform: 'uppercase', letterSpacing: 1 },
    title: { fontSize: 12, color: theme.titleColor, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 },

    // Grid Layout
    gridContainer: { flexDirection: 'row', gap: 20 },
    leftColumn: { width: '65%', backgroundColor: theme.leftColumnBackground, padding: theme.leftColumnPadding },
    rightColumn: { width: '35%', backgroundColor: theme.rightColumnBackground, borderLeft: 1, borderLeftColor: '#e5e7eb', paddingLeft: 20 },

    // Dynamic styles for sections based on column
    sectionTitleLeft: { fontSize: 12, fontWeight: 'bold', color: theme.leftHeadingColor, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 1 },
    separatorLeft: { height: 1, backgroundColor: theme.leftSeparatorColor, width: '30%', marginBottom: 10, opacity: 0.5 },

    sectionTitleRight: { fontSize: 12, fontWeight: 'bold', color: theme.rightHeadingColor, textTransform: 'uppercase', marginBottom: 10, letterSpacing: 1 },
    separatorRight: { height: 1, backgroundColor: theme.rightSeparatorColor, width: '30%', marginBottom: 10, opacity: 0.5 },

    profileImage: {
        width: customization.imageSize,
        height: customization.imageSize,
        borderRadius: customization.imageShape === 'circle' ? customization.imageSize / 2 :
            customization.imageShape === 'rounded' ? 10 : 0.01,
        backgroundColor: '#f3f4f6', marginBottom: 15,
        alignSelf: 'flex-start'
    },
    initials: { fontSize: 24, color: theme.textColor, fontWeight: 'bold' },

    subtitle: { fontSize: 11, fontWeight: 'bold', color: theme.textColor, marginBottom: 1 },
    company: { fontSize: 10, color: theme.primary, fontWeight: 'medium', marginBottom: 2 },
    meta: { fontSize: 9, color: '#666', marginBottom: 5 },
    text: { fontSize: 9.5, lineHeight: 1.5, color: theme.textColor },

    skillItem: { backgroundColor: '#f3f4f6', padding: '4 8', borderRadius: 0, marginBottom: 4, fontSize: 9, color: theme.textColor },
    contactMeta: { fontSize: 9, color: theme.textColor, marginBottom: 4 }
});

const RenderSharpTwoColumn = ({ data, isPaid }) => {
    const { personal_info = {}, education = [], work_experience = [], skills = [], colors = {}, customization = {} } = safeData(data);

    // Extensive Dynamic Theme Config
    const baseHeadingColor = colors.titleColor || colors.headingColor || '#111111';
    const baseSeparatorColor = colors.titleColor || colors.separatorColor || (colors.secondary || '#4b5563');

    const theme = {
        primary: colors.titleColor || colors.primary || '#1e40af',
        secondary: colors.textColor || colors.secondary || '#4b5563',
        pageBackground: colors.backgroundColor || colors.pageBackground || '#ffffff',
        textColor: colors.textColor || '#333333',
        nameColor: colors.titleColor || colors.nameColor || '#111111',
        titleColor: colors.titleColor || (colors.primary || '#1e40af'),
        headerLineColor: colors.titleColor || colors.headerLineColor || (colors.primary || '#1e40af'),

        leftColumnBackground: colors.sidebarBackgroundColor || colors.leftColumnBackground || 'transparent',
        leftColumnPadding: (colors.sidebarBackgroundColor || colors.leftColumnBackground) ? 15 : 0,
        rightColumnBackground: colors.backgroundColor || colors.rightColumnBackground || 'transparent',

        leftHeadingColor: colors.sidebarTitleColor || colors.leftHeadingColor || baseHeadingColor,
        leftSeparatorColor: colors.sidebarTitleColor || colors.leftSeparatorColor || baseSeparatorColor,

        rightHeadingColor: colors.titleColor || colors.rightHeadingColor || baseHeadingColor,
        rightSeparatorColor: colors.titleColor || colors.rightSeparatorColor || baseSeparatorColor,
    };

    // Image Customization Config
    const customConfig = {
        imageSize: customization.imageSize || 60,
        imageShape: customization.imageShape || 'square', // 'circle', 'square', 'rounded'
    };

    const calculatedBorderRadius = customConfig.imageShape === 'circle' ? customConfig.imageSize / 2 :
        customConfig.imageShape === 'rounded' ? 10 : 0.01;

    const styles = getStylesSharpTwoColumn(theme, customConfig);

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <View style={styles.headerLine}></View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View>
                        <Text style={styles.name}>{personal_info.full_name || 'YOUR NAME'}</Text>
                        <Text style={styles.title}>{personal_info.title || 'PROFESSIONAL TITLE'}</Text>
                    </View>
                    <ProfileInitials fullName={personal_info.full_name} styles={styles} profilePicture={personal_info.profile_picture} borderRadius={calculatedBorderRadius} />
                </View>
            </View>

            <View style={styles.gridContainer}>
                <View style={styles.leftColumn}>
                    {personal_info.summary && (
                        <View style={{ marginBottom: 25 }}>
                            <Text style={styles.sectionTitleLeft}>Profile</Text>
                            <View style={styles.separatorLeft}></View>
                            <Text style={styles.text}>{personal_info.summary}</Text>
                        </View>
                    )}

                    {work_experience.length > 0 && (
                        <View>
                            <Text style={styles.sectionTitleLeft}>Experience</Text>
                            <View style={styles.separatorLeft}></View>
                            {work_experience.map((exp, index) => (
                                <View key={index} style={{ marginBottom: 15 }}>
                                    <Text style={styles.subtitle}>{exp.position}</Text>
                                    <Text style={styles.company}>{exp.company}</Text>
                                    <Text style={styles.meta}>{exp.start_date} - {exp.current ? 'Present' : exp.end_date}</Text>
                                    <Text style={styles.text}>{exp.description}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                </View>

                <View style={styles.rightColumn}>
                    <View style={{ marginBottom: 25 }}>
                        <Text style={styles.sectionTitleRight}>Contact</Text>
                        <View style={styles.separatorRight}></View>
                        <Text style={styles.contactMeta}>{personal_info.email}</Text>
                        <Text style={styles.contactMeta}>{personal_info.phone}</Text>
                        <Text style={styles.contactMeta}>{personal_info.address}</Text>
                        <Text style={styles.contactMeta}>{personal_info.linkedin}</Text>
                    </View>

                    {skills.length > 0 && (
                        <View style={{ marginBottom: 25 }}>
                            <Text style={styles.sectionTitleRight}>Skills</Text>
                            <View style={styles.separatorRight}></View>
                            {skills.map((skill, index) => (
                                <Text key={index} style={styles.skillItem}>{skill}</Text>
                            ))}
                        </View>
                    )}

                    {education.length > 0 && (
                        <View>
                            <Text style={styles.sectionTitleRight}>Education</Text>
                            <View style={styles.separatorRight}></View>
                            {education.map((edu, index) => (
                                <View key={index} style={{ marginBottom: 10 }}>
                                    <Text style={{ fontWeight: 'bold', fontSize: 10 }}>{edu.degree}</Text>
                                    <Text style={{ fontSize: 9 }}>{edu.institution}</Text>
                                    <Text style={{ fontSize: 9, color: '#777' }}>{edu.start_date} - {edu.end_date}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            </View>
            <Watermark isPaid={isPaid} />
        </Page>
    );
};


const ResumeTemplate = ({ data, isPaid }) => {

    let TemplateComponent = RenderBlue; // Default to Template 1
    let templateIdKey = 'template-default';

    if (data && data.template_id) {
        const templateIdLower = data.template_id.toLowerCase();
        templateIdKey = templateIdLower;

        switch (templateIdLower) {
            case 'template1':
                TemplateComponent = RenderBlue;
                break;
            case 'template2':
                TemplateComponent = RenderMinimalist;
                break;
            case 'template3':
                TemplateComponent = RenderTwoColumn;
                break;
            case 'template4':
                TemplateComponent = RenderGeometric;
                break;
            case 'template5':
                TemplateComponent = RenderTechStack;
                break;
            case 'template6':
                TemplateComponent = RenderFreelancer;
                break;
            case 'template7':
                TemplateComponent = RenderArchitect;
                break;
            case 'template8':
                TemplateComponent = RenderSimpleProfessional;
                break;
            case 'template9':
                TemplateComponent = RenderAsymmetrical;
                break;
            case 'template10':
                TemplateComponent = RenderVibrantCompact;
                break;
            case 'template11':
                TemplateComponent = RenderSplitModern;
                break;
            case 'template12':
                TemplateComponent = RenderSharpTwoColumn;
                break;
            default:
                TemplateComponent = RenderBlue; // Fallback
        }
    }

    return (
        <Document>
            <TemplateComponent data={data} isPaid={isPaid} key={templateIdKey} />
        </Document>
    );
};

export default ResumeTemplate;